// feat-004 story 5.5 (FR-49 stage 2; AD-12) — per-network, data-driven
// displayOptionsEnabled flag carrier for the frontend.
//
// No chain-specific settings store exists elsewhere in this app (verified
// this session): App.js's loadGlobalSettings() keeps its result in component
// state (not subscribable), and signState.signinDetails is populated once at
// login and isn't chain-settings shaped. This module is a small,
// framework-free subscribe/notify store so both class components (via
// subscribe) and the V3 hook (useDisplayOptionsEnabled) can read the same
// value without prop drilling.
//
// Safe-false default: until loaded, and on any load failure, the flag reads
// false — matching the backend's NOT NULL DEFAULT 0 column semantics
// (feature is opt-in per chain).
//
// submitSets/submitCollection are required LAZILY (inside loadChainSettings,
// not at module scope) — a top-level import here drags the legacy service
// layer's eager i18n bootstrap (submit.service -> common.service -> i18n.js)
// into every V3 test that imports this module transitively via the
// useDisplayOptionsEnabled hook, breaking suites that mock react-i18next
// with a stub lacking `initReactI18next` (same lazy-require pattern already
// used by ExportMenu.tsx's `getFieldwiseExcel`).

let displayOptionsEnabled = false;
let hasLoaded = false;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener(displayOptionsEnabled));
}

export function getDisplayOptionsEnabled() {
  return displayOptionsEnabled;
}

export function hasLoadedDisplayOptionsEnabled() {
  return hasLoaded;
}

export function subscribeDisplayOptionsEnabled(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function loadChainSettings() {
  // Code review 2026-08-18: the two lazy requires below used to sit
  // outside this try — a synchronous throw (module error, or a broken
  // destructure) would escape uncaught instead of falling into the
  // documented safe-false contract. Wrapped so EVERY failure path,
  // synchronous or async, lands on the same fallback.
  try {
    // eslint-disable-next-line global-require
    const { submitSets } = require('../components/UiComponents/SubmitSets');
    // eslint-disable-next-line global-require
    const { submitCollection } = require('./submit.service');
    return submitSets(submitCollection.getDisplayOptionsFlag, null, false)
      .then((res) => {
        const next = !!(res && res.status && res.extra && res.extra.displayOptionsEnabled);
        hasLoaded = true;
        if (next !== displayOptionsEnabled) {
          displayOptionsEnabled = next;
        }
        notify();
      })
      .catch(() => {
        // feat-004 story 5.5 (AD-12): any failure (network, auth, missing
        // setting) falls back to the safe default rather than throwing.
        displayOptionsEnabled = false;
        hasLoaded = true;
        notify();
      });
  } catch (e) {
    displayOptionsEnabled = false;
    hasLoaded = true;
    notify();
    return Promise.resolve();
  }
}

// test-only reset hook
export function __resetChainSettingsStoreForTests() {
  displayOptionsEnabled = false;
  hasLoaded = false;
  listeners.clear();
}
